import {BlazeConfig} from 'blaze-slider'
import React, {useMemo} from 'react'
import clsx from 'clsx'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductImageBaseUrl,
    getProductModelImageSrc,
    getProductModelImageSrcSet,
    ModelImage,
} from '~/utils/product'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import ImageComposition from '~/components/ImageComposition'
import Carousel from '~/components/common/Carousel'
import {Image} from '~/gql/graphql'

type ProductImageSliderProps = {
    className?: string
    selectedAppearanceId: string
    product: ProductWithIds & {images: ModelImage[]}
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1,
        loop: false,
    },
}

const ProductImageSlider = ({
    className,
    product,
    selectedAppearanceId,
}: ProductImageSliderProps) => {
    const images = useMemo<Image[]>(() => {
        const baseProductImageSrc = getProductImageBaseUrl(product.image?.src)!
        const baseDesignImageSrc = getCompositionImageBaseUrl(product.image?.src)!
        const viewId = product.defaultViewId ?? '1'

        const filteredImages = product.images.filter(
            (image) => image.appearanceId === selectedAppearanceId
        )

        const images = filteredImages.map((image) => ({
            src: getProductModelImageSrc(
                baseProductImageSrc,
                image.viewId!,
                image.appearanceId!,
                image.modelId,
                image.crop
            ),
            srcset: getProductModelImageSrcSet(
                baseProductImageSrc,
                image.viewId!,
                image.appearanceId!,
                image.modelId,
                image.crop
            ),
            altText: product.image?.altText,
        }))

        const designImage = {
            src: getDesignImageSrc(baseDesignImageSrc, viewId, selectedAppearanceId),
            srcset: getDesignImageSrcSet(baseDesignImageSrc, viewId, selectedAppearanceId),
            altText: product.image?.altText,
        }

        return images.length > 0
            ? [images[0], designImage, ...images.slice(1, images.length)]
            : [
                  {
                      src: getProductModelImageSrc(
                          baseProductImageSrc,
                          viewId,
                          selectedAppearanceId
                      ),
                      srcset: getProductModelImageSrcSet(
                          baseProductImageSrc,
                          viewId,
                          selectedAppearanceId
                      ),
                      altText: product.image?.altText,
                  },
                  designImage,
              ]
    }, [
        selectedAppearanceId,
        product.image?.src,
        product.images,
        product.image?.altText,
        product.defaultViewId,
    ])

    return (
        <Carousel
            className={clsx(className)}
            config={config}
            showDots={true}
            showArrows={true}
            initialSlidesToShow={1}
        >
            {images.map((image, index) => (
                <ImageComposition
                    className="drop-shadow-lg p-4"
                    imageClassName="rounded-md overflow-hidden select-none"
                    lazy={index !== 0}
                    image={image}
                    sizes="(min-width: 640px) 1000px, 500px"
                    key={index}
                />
            ))}
        </Carousel>
    )
}

export default ProductImageSlider
